import { Alert, Button, Snackbar } from "@mui/material";
import Fade, { FadeProps } from "@mui/material/Fade";
import type { PimoReactComponent } from "@pimo/pimo-app-builder";
import { useState } from "react";
import { Outlet } from "react-router-dom";

export type SnackbarOverlayProps = {
  snackbar?: {
    message: string;
    severity: "success" | "info" | "warning" | "error";
    open: boolean;
    type: "snackbar" | "alert";
    /** Adds a button that copies `text`, e.g. a reference id for support. */
    copy?: { text: string; label: string; copiedLabel: string };
  };
};

export type SnackbarOverlayEventNames = "overlay:close-snackbar";

function FadeTransition(props: FadeProps) {
  return <Fade {...props} timeout={1000} />;
}

export const SnackbarOverlay: PimoReactComponent<
  SnackbarOverlayProps,
  SnackbarOverlayEventNames,
  undefined
> = ({ fireEvent, snackbar }) => {
  const [snackbarOpen, setSnackbarOpen] = useState(Boolean(snackbar));
  const [prevSnackbar, setPrevSnackbar] = useState(snackbar);
  const [copied, setCopied] = useState(false);

  if (prevSnackbar !== snackbar) {
    setPrevSnackbar(snackbar);
    setSnackbarOpen(snackbar?.open ?? false);
    setCopied(false);
  }

  const copy = snackbar?.copy;

  return (
    <div>
      <Outlet />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={copy ? 15000 : 5000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
        onClose={() => {
          setSnackbarOpen(false);
          setTimeout(() => {
            fireEvent?.("overlay:close-snackbar");
          }, 1000);
        }}
        TransitionComponent={FadeTransition}
      >
        <Alert
          severity={snackbar?.severity}
          variant="filled"
          action={
            copy ? (
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  navigator.clipboard
                    ?.writeText(copy.text)
                    .then(() => setCopied(true))
                    .catch(() => undefined);
                }}
              >
                {copied ? copy.copiedLabel : copy.label}
              </Button>
            ) : undefined
          }
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minWidth: "300px",
            ...(snackbar?.severity === "info" && {
              backgroundColor: "#3ec6ff",
              border: "none",
              "& .MuiAlert-message": { color: "white", fontWeight: 500 },
              "& .MuiAlert-icon": { color: "white" },
            }),
            ...(snackbar?.severity === "warning" && {
              "& .MuiAlert-message": {
                color: "rgba(0, 0, 0, 0.87)",
                fontWeight: 500,
              },
              "& .MuiAlert-icon": { color: "rgba(0, 0, 0, 0.87)" },
            }),
            ...((snackbar?.severity === "success" ||
              snackbar?.severity === "error") && {
              "& .MuiAlert-message": { color: "white", fontWeight: 500 },
              "& .MuiAlert-icon": { color: "white" },
            }),
          }}
        >
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </div>
  );
};
